import type { TntJobV2 } from '../../store/services/TimeInTransitService/types';

// The v2 upload API is asynchronous: create/update returns 200 with uploadJobStatus
// 'IN_PROGRESS' and the file is parsed afterwards. Reporting success on that response tells
// the uploader their file landed even when it later fails validation wholesale, so we poll
// for the terminal status before saying anything.

export const DEFAULT_POLL_INTERVAL_MS = 2_000;
export const DEFAULT_POLL_TIMEOUT_MS = 90_000;

export type UploadJobPollResult =
  | { status: 'COMPLETE'; job: TntJobV2 }
  | { status: 'FAILED'; job: TntJobV2 }
  | { status: 'TIMED_OUT'; job?: TntJobV2 };

export interface PollUploadJobArgs {
  jobId: string;
  // Returns the job. The store thunks catch and RETURN their error instead of throwing, so this
  // may hand back an Error — treated as transient and retried until the timeout.
  fetchJob: (jobId: string) => Promise<unknown>;
  sleep: (ms: number) => Promise<void>;
  intervalMs?: number;
  timeoutMs?: number;
}

export const isTerminalStatus = (status: unknown): status is 'COMPLETE' | 'FAILED' =>
  status === 'COMPLETE' || status === 'FAILED';

const asJob = (value: unknown): TntJobV2 | undefined => {
  if (!value || typeof value !== 'object' || value instanceof Error) {
    return undefined;
  }
  return typeof (value as TntJobV2).uploadJobStatus === 'string' ? (value as TntJobV2) : undefined;
};

export const pollUploadJob = async ({
  jobId,
  fetchJob,
  sleep,
  intervalMs = DEFAULT_POLL_INTERVAL_MS,
  timeoutMs = DEFAULT_POLL_TIMEOUT_MS,
}: PollUploadJobArgs): Promise<UploadJobPollResult> => {
  const maxAttempts = Math.max(1, Math.ceil(timeoutMs / intervalMs));
  let lastSeen: TntJobV2 | undefined;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const job = asJob(await fetchJob(jobId));

    if (job) {
      lastSeen = job;
      if (isTerminalStatus(job.uploadJobStatus)) {
        return { status: job.uploadJobStatus, job };
      }
    }

    if (attempt < maxAttempts - 1) {
      await sleep(intervalMs);
    }
  }

  return { status: 'TIMED_OUT', job: lastSeen };
};
